import {
  EKycVerificationStatus,
  useEcGetEKycDocumentDownloadSignedUrlQuery,
  useEcUpdateEKycStatusMutation,
  GetEKycInfoByExamineeIdAndExamEventIdDocument,
} from '@exam-center/graphql';
import { APP_PERMISSIONS } from '@exam-center/permission';
import { useNavigate, useParams } from '@exam-center/router';
import { Loading, Flex, DescriptionOutlinedIcon, Text, Paper } from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useLogger } from '@shared/logger';
import { useHasPermission } from '@shared/permissions';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { ProctoringEkycVerificationForm } from '../../components';
import { useProctoringEKycVerification } from '../../providers';
import { AlreadyVerifiedModal } from '../already-verified-modal';

import {
  ProctoringEkycVerificationFormSchema,
  type ProctoringEkycVerificationFormType,
} from './schema';

export default function ProctoringEkycVerificationLogic() {
  const canEdit = useHasPermission(APP_PERMISSIONS.PROCTORING.E_KYC.UPDATE);

  const {
    eKycInfo,
    examCenterCode,
    examEventId,
    loading: isLoading,
  } = useProctoringEKycVerification();
  const {
    title,
    firstName,
    middleName,
    lastName,
    idNumber,
    dateOfBirth,
    faceMatched,
    status: eKycStatus,
    comment,
    verificationCheckList,
    documents,
  } = eKycInfo;

  const formContext = useForm<ProctoringEkycVerificationFormType>({
    disabled: eKycStatus === EKycVerificationStatus.Rejected,
    defaultValues: {
      photoClarity: undefined,
      photoIdentityMatch: undefined,
      idPhotoClarity: undefined,
      verificationStatus: EKycVerificationStatus.PendingApproval,
      comment,
    },
    resolver: zodResolver(ProctoringEkycVerificationFormSchema),
  });

  const { photoClarity, photoIdentityMatch, idPhotoClarity } = useWatch({
    control: formContext.control,
  });

  const [isOpen, { open, close }] = useDisclosure(false);
  const [isVerifiedModalOpen, { open: openVerifiedModal }] = useDisclosure(false);

  const logger = useLogger();

  const { examineeId } = useParams(
    '/:examCenterCode/proctoring/exam-events/:examEventId/e-kyc/:examineeId',
  );

  const navigate = useNavigate();

  const [updateEKycVerificationStatus, { loading }] = useEcUpdateEKycStatusMutation();

  const { data: signedUrls, loading: isGettingSignedUrls } =
    useEcGetEKycDocumentDownloadSignedUrlQuery({
      variables: {
        getEKycDocumentDownloadSignedUrlInput: {
          fileNames: documents
            .map((document) => document.storageObject.key)
            .filter((key): key is string => key !== null),
          examEventId,
          examineeId,
        },
      },
    });

  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    const isRejected = [photoClarity, photoIdentityMatch, idPhotoClarity].includes('INCORRECT');
    const isApproved = [photoClarity, photoIdentityMatch, idPhotoClarity].every(
      (status) => status === 'CORRECT',
    );
    const status = isRejected
      ? EKycVerificationStatus.Rejected
      : isApproved
        ? EKycVerificationStatus.Approved
        : EKycVerificationStatus.PendingApproval;

    formContext.setValue('verificationStatus', status);
  }, [formContext, photoClarity, photoIdentityMatch, idPhotoClarity]);

  useEffect(() => {
    formContext.reset({
      photoClarity: getStatus(verificationCheckList.photoClarity),
      photoIdentityMatch: getStatus(verificationCheckList.photoIdentityMatch),
      idPhotoClarity: getStatus(verificationCheckList.idPhotoClarity),
      verificationStatus: eKycStatus,
      comment: comment,
    });
  }, [formContext, verificationCheckList, eKycStatus, comment]);

  if (isLoading || isGettingSignedUrls) {
    return (
      <Loading
        sx={{
          width: '100%',
        }}
      />
    );
  }

  if (eKycStatus === EKycVerificationStatus.Approved) {
    return (
      <Paper
        sx={(theme) => ({
          width: '100%',
          height: theme.spacing(100),
        })}
      >
        <Flex
          sx={(theme) => ({
            flexDirection: 'column',
            gap: theme.spacing(6),
            p: theme.spacing(6),
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          })}
        >
          <DescriptionOutlinedIcon sx={{ fontSize: 100, color: 'grey.400' }} />
          <Text variant='h3'>ผู้เข้าสอบรายนี้ได้รับการตรวจสอบแล้ว</Text>
        </Flex>
      </Paper>
    );
  }

  const handleSubmit = async (data: ProctoringEkycVerificationFormType) => {
    updateEKycVerificationStatus({
      variables: {
        updateEKycStatusInput: {
          examineeId,
          examEventId,
          status: data.verificationStatus as EKycVerificationStatus,
          comment: data.comment,
          verificationCheckList: {
            photoClarity: data.photoClarity === 'CORRECT',
            photoIdentityMatch: data.photoIdentityMatch === 'CORRECT',
            idPhotoClarity: data.idPhotoClarity === 'CORRECT',
          },
        },
      },
      awaitRefetchQueries: true,
      refetchQueries: [GetEKycInfoByExamineeIdAndExamEventIdDocument],
      onCompleted: () => {
        showSnackbar({
          message: 'พิจารณาการยืนยันตัวตนเรียบร้อยแล้ว',
          variant: 'success',
        });
        navigate('/:examCenterCode/proctoring/exam-events/:examEventId/e-kyc', {
          params: {
            examCenterCode: examCenterCode,
            examEventId: examEventId,
          },
        });
      },
      onError: (error) => {
        if (error.message.includes('not allowed to be updated')) {
          close();
          openVerifiedModal();
        } else {
          logger.error('Submit e-KYC verification form error', error);
          showSnackbar({
            message: 'เกิดข้อผิดพลาดในการพิจารณาการยืนยันตัวตน',
            variant: 'error',
          });
        }
      },
    });

    logger.info('Submit e-KYC verification form', data);
    open();
  };

  return (
    <>
      <ProctoringEkycVerificationForm
        formContext={formContext}
        onSubmit={handleSubmit}
        modalProps={{
          open: isOpen,
          onClose: close,
          onOpen: open,
          loading,
        }}
        slotProps={{
          name: {
            title,
            firstName,
            middleName,
            lastName,
          },
          document: {
            idNumber,
          },
          dateOfBirth: dateOfBirth,
          image: {
            idCardImage:
              signedUrls?.ecGetEKycDocumentDownloadSignedUrl.signedUrls[
                documents.find((document) => document.type === 'CITIZEN_CARD_FRONT')?.storageObject
                  .key ?? ''
              ],
            faceImage:
              signedUrls?.ecGetEKycDocumentDownloadSignedUrl.signedUrls[
                documents.find((document) => document.type === 'FACE_IMAGE')?.storageObject.key ??
                  ''
              ],
          },
          status: {
            eKycStatus,
          },
          faceMatched: faceMatched,
        }}
        examCenterCode={examCenterCode}
        examEventId={examEventId}
        disabled={!canEdit}
      />
      <AlreadyVerifiedModal
        open={isVerifiedModalOpen}
        examEventId={examEventId}
        examCenterCode={examCenterCode}
      />
    </>
  );
}

function getStatus(value: boolean | null) {
  if (value === true) return 'CORRECT';
  if (value === false) return 'INCORRECT';
  return undefined;
}
